/**
 * Sponsor Request Page
 *
 * Multi-step flow for sponsors to create advisory requests
 * Steps:
 * 1. Fill form (DynamicFormGroup - sponsor-advisory-form)
 * 2. Set availability (redirect to AgendaConfiguration)
 * 3. Confirmation
 */

import React, { useState, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { Box, LinearProgress } from '@mui/material';

import ConfirmationStep from './components/ConfirmationStep';
import MyLeadRequestList from './components/MyLeadRequestList';
import RequestForm from './components/RequestForm';
import { useSponsorRequest } from './hooks/useSponsorRequest';
import * as S from './styles';
import Button from 'components/Button';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { useUser } from 'global/UserContext';
import { useFetchCompleteForm } from 'hooks/useFetchCompleteForm';
import AgendaConfiguration from 'pages/AgendaConfiguration';

enum Step {
  INITIAL = 0,
  FORM = 1,
  AVAILABILITY = 2,
  CONFIRMATION = 3,
}

export default function SponsorLeadRequest() {
  const [currentStep, setCurrentStep] = useState<Step>(Step.INITIAL);
  const [requestId, setRequestId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { createRequest, loading: creatingRequest, error } = useSponsorRequest();
  const { user } = useUser();

  // Fetch form data to check if user has filled it
  const {
    formData,
    loading: loadingForm,
    formGroupId,
  } = useFetchCompleteForm(user?.id || '', null, 'sponsor-lead-request-form');

  const handleFormDataReady = useCallback((hasData: boolean) => {
    if (hasData) {
      setCurrentStep(Step.AVAILABILITY);
    }
  }, []);

  const handleFormComplete = useCallback(async () => {
    // Get form responses from the form data
    // The DynamicFormGroup auto-saves, so we need to fetch the saved data
    if (!formData || formData.length === 0) {
      return;
    }

    // Convert form fields with submissions to JSON format
    const form = formData[0];
    const formResponses: Record<string, any> = {};

    form.fields.forEach(field => {
      if (field.submission && field.submission.values) {
        // Extract the values from submission
        const values = field.submission.values.map(v => v.value);
        formResponses[field.label] = values.length === 1 ? values[0] : values;
      }
    });

    // Create the request
    const response = await createRequest(formResponses);

    if (response) {
      setRequestId(response.id);
      setCurrentStep(Step.AVAILABILITY);
    }
  }, [formData, createRequest]);

  const handleAvailabilityComplete = useCallback(() => {
    setCurrentStep(Step.CONFIRMATION);
  }, []);

  const handleConfirmAvailability = useCallback(() => {
    setCurrentStep(Step.CONFIRMATION);
  }, []);

  const handleGoToLeadRequestPanel = useCallback(() => {
    setCurrentStep(Step.INITIAL);
  }, []);

  const progress = ((currentStep - 1) / 2) * 100;

  if (loadingForm) {
    return (
      <S.Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Spin />
        </Box>
      </S.Container>
    );
  }

  if (currentStep === Step.INITIAL) {
    return <MyLeadRequestList onGoToForm={() => setCurrentStep(Step.FORM)} />;
  }

  return (
    <S.Container>
      {/* Header */}
      <S.Header>
        <S.Title>Criar solicitação de lead</S.Title>
        <Text size="medium" color="textSecondary">
          Complete o passo a passo abaixo para solicitar leads
        </Text>
      </S.Header>

      {/* Progress Bar */}
      <S.ProgressSection>
        <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
        <S.StepsContainer>
          <S.StepLabel active={currentStep === Step.FORM}>
            <S.StepNumber active={currentStep === Step.FORM}>1</S.StepNumber>
            <Text size="small" bold={currentStep === Step.FORM}>
              Preencher formulário
            </Text>
          </S.StepLabel>
          <S.StepLabel active={currentStep === Step.AVAILABILITY}>
            <S.StepNumber active={currentStep === Step.AVAILABILITY}>2</S.StepNumber>
            <Text size="small" bold={currentStep === Step.AVAILABILITY}>
              Configurar disponibilidade
            </Text>
          </S.StepLabel>
          <S.StepLabel active={currentStep === Step.CONFIRMATION}>
            <S.StepNumber active={currentStep === Step.CONFIRMATION}>3</S.StepNumber>
            <Text size="small" bold={currentStep === Step.CONFIRMATION}>
              Confirmação
            </Text>
          </S.StepLabel>
        </S.StepsContainer>
      </S.ProgressSection>

      {/* Content */}
      <S.ContentSection>
        {currentStep === Step.FORM && formGroupId && (
          <>
            <RequestForm formGroupId={formGroupId} onFormDataReady={handleFormDataReady} />
            <S.ActionButtons>
              <Button variant="outline" onClick={() => setCurrentStep(Step.INITIAL)} size="medium">
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleFormComplete}
                loading={creatingRequest}
                disabled={creatingRequest}
                size="medium"
              >
                Próximo: Configurar disponibilidade
              </Button>
            </S.ActionButtons>
          </>
        )}

        {currentStep === Step.AVAILABILITY && (
          <S.AvailabilitySection>
            <Box mb={3}>
              <Text size="large" bold textAlign="center">
                Configurar sua disponibilidade
              </Text>
              <Text size="medium" textAlign="center" color="textSecondary">
                Configure os horários disponíveis para as reuniões de consultoria
              </Text>
            </Box>

            {/* <S.InfoBox>
              <Text size="medium" color="textPrimary">
                📅 Você será redirecionado para a página de Configuração de Agenda onde você pode
                definir seus horários disponíveis.
              </Text>
            </S.InfoBox> */}
            <AgendaConfiguration />

            <S.ActionButtons>
              <Button variant="outline" onClick={() => setCurrentStep(Step.FORM)} size="medium">
                Voltar para o formulário
              </Button>
              <Button variant="primary" onClick={handleConfirmAvailability} size="medium">
                Confirmar
              </Button>
            </S.ActionButtons>

            <Box mt={3} textAlign="center">
              <Button
                variant="minimal"
                onClick={handleAvailabilityComplete}
                size="small"
                style={{ textDecoration: 'underline' }}
              >
                Pular para agora (Vou definir mais tarde)
              </Button>
            </Box>
          </S.AvailabilitySection>
        )}

        {currentStep === Step.CONFIRMATION && requestId && (
          <ConfirmationStep
            requestId={requestId}
            handleGoToLeadRequestPanel={handleGoToLeadRequestPanel}
          />
        )}
      </S.ContentSection>

      {/* Error Display */}
      {error && (
        <Box mt={2}>
          <Text size="medium" color="red" textAlign="center">
            {error}
          </Text>
        </Box>
      )}
    </S.Container>
  );
}
